<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import store from '@/store'
import { mdiArrowLeft } from '@mdi/js'
import { Studio } from '@/modules/Studios/types'
import { StudioApi } from '@/enitites/studio'
import BKConfirmationDialog from '@/shared/ui/modals/BKConfirmationDialog.vue'
import Bugsnag from '@bugsnag/js'

const studio = ref<Studio>()
const isLoading = ref(false)
const showConfirmationDialog = ref(false)

const route = useRoute()
const router = useRouter()

const buttonText = computed(() => {
  if (!studio.value) return 'Deactivate Studio'
  return studio.value.status === 'draft' ? 'Publish Studio' : 'Deactivate Studio'
})

const confirmationTitle = computed(() => {
  if (!studio.value) return ''
  return studio.value.status === 'draft' ? `Publish ${studio.value.name}?` : `Deactivate ${studio.value.name}?`
})

const confirmationText = computed(() => {
  if (!studio.value) return ''
  return studio.value.status === 'draft'
    ? 'This will make your studio visible to the public.'
    : 'This will hide your studio from the public.'
})

const confirmationButtonText = computed(() => {
  if (!studio.value) return 'Confirm'
  return studio.value.status === 'draft' ? 'Publish' : 'Deactivate'
})

const currentRoute = computed(() => route.matched.find((routeItem) => routeItem.parent))
const childRoutes = computed(
  () => router.options.routes?.find((item) => item.name === 'StudioSettings')?.children || []
)

onMounted(async () => {
  try {
    studio.value = await StudioApi.fetchStudio(route.params.id)
    if (!studio.value) {
      await router.push({ name: 'Studios' })
    }
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error fetching studio',
        studioId: route.params.id,
      })
    })
    await router.push({ name: 'Studios' })
  }
})

async function updateStudioData() {
  if (!studio.value) return
  isLoading.value = true
  try {
    const resp = await StudioApi.updateStudio(studio.value)
    if (resp) {
      await store.dispatch('showAlertToast', 'changesSaved')
    }
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error updating studio',
        studioId: studio.value?.id,
      })
    })
    await store.dispatch('showAlertToast', 'error')
  } finally {
    isLoading.value = false
  }
}

function openConfirmationDialog() {
  showConfirmationDialog.value = true
}

async function toggleStudioStatus() {
  if (!studio.value) return

  isLoading.value = true
  try {
    if (studio.value.status === 'draft') {
      // Publish the studio
      await StudioApi.publishStudio(studio.value.id)
      studio.value.status = 'published'
      await store.dispatch('showAlertToast', 'studioPublished')
    } else {
      // Set to draft
      await StudioApi.unpublishStudio(studio.value.id)
      studio.value.status = 'draft'
      await store.dispatch('showAlertToast', 'studioDeactivated')
    }
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error toggling studio status',
        studioId: studio.value?.id,
        targetStatus: studio.value?.status === 'draft' ? 'published' : 'draft',
      })
    })
    await store.dispatch('showAlertToast', 'error')
  } finally {
    isLoading.value = false
    showConfirmationDialog.value = false
  }
}
</script>

<template>
  <section :class="['studio-settings', { 'child-route': currentRoute }]">
    <BKConfirmationDialog
      :is-show="showConfirmationDialog"
      :title="confirmationTitle"
      :confirm-text="confirmationButtonText"
      @confirm="toggleStudioStatus"
      @cancel="showConfirmationDialog = false"
    >
      {{ confirmationText }}
    </BKConfirmationDialog>

    <v-progress-circular
      v-if="!studio"
      class="loader"
      color="#FC7800"
      indeterminate
      size="200"
      width="15"
    />
    <div
      v-else
      class="wrap"
    >
      <h2 :class="['studio-title', 'page-title', { 'child-route': currentRoute }]">
        <router-link :to="{ name: 'Studio', params: { id: String(studio?.id) } }">
          <v-icon
            alt="Back to studio page"
            aria-hidden="false"
            class="title-arrow"
          >
            {{ mdiArrowLeft }}
          </v-icon>
        </router-link>
        {{ studio.name }}
      </h2>
      <div class="studio-container">
        <div :class="['navigation', { 'child-route': currentRoute }]">
          <router-link
            v-for="(childRoute, index) in childRoutes"
            :key="index"
            :to="{ name: childRoute.name }"
            active-class="active"
            class="item"
          >
            {{ childRoute.meta?.title }}
          </router-link>

          <v-btn
            class="deactivate-button"
            text
            height="57"
            @click="openConfirmationDialog"
          >
            {{ buttonText }}
          </v-btn>
        </div>
        <div class="setting-container">
          <h2
            v-if="currentRoute"
            class="studio-title page-title child-route-title"
          >
            <router-link :to="{ name: 'StudioSettings' }">
              <v-icon
                alt="Back to list of settings"
                aria-hidden="false"
                class="title-arrow"
              >
                {{ mdiArrowLeft }}
              </v-icon>
            </router-link>
            {{ currentRoute.meta.title }}
          </h2>

          <transition
            :name="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'slide-fade' : ''"
            mode="out-in"
          >
            <router-view
              :is-loading="isLoading"
              :studio="studio"
              @update="updateStudioData"
            />
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.loader {
  display: flex !important;
  margin: 210px auto;
}

.confirm-alert {
  position: absolute;
  z-index: 1;
  width: 100%;
  font-weight: var(--font-weight-bold);
}

.studio-settings {
  padding-top: var(--header-height);
  background-color: var(--color-grey-100);

  &.child-route {
    @media (max-width: 768px) {
      background-color: var(--color-white);
    }
  }
}

.wrap {
  width: 100%;
  max-width: 880px;
  margin: 40px auto;

  .studio-title {
    @include font-h2;

    display: flex;
    align-items: center;
    margin-bottom: 20px;

    &.child-route {
      display: block;

      @media (max-width: 768px) {
        display: none;
      }
    }

    &.child-route-title {
      display: none;

      @media (max-width: 768px) {
        display: block;
      }
    }

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }

  .title-arrow {
    @include font-h2;

    margin-top: -6px;
    margin-left: -40px;
    color: var(--color-black);
    cursor: pointer;

    @media (max-width: 768px) {
      margin-left: 0;
    }
  }

  @media (max-width: 768px) {
    margin: 40px auto;
  }
}

.studio-container {
  display: flex;
  height: 620px;
  background-color: var(--color-white);
  border-radius: var(--border-radius-rounded);
  overflow: hidden;

  // TODO: Move this to globals
  box-shadow: 0 2px 8px rgb(0 0 0 / 10%);

  .navigation {
    display: flex;
    flex-direction: column;
    min-width: 190px;
    background-color: var(--color-grey-600);
    border-top-left-radius: var(--border-radius-rounded);
    border-bottom-left-radius: var(--border-radius-rounded);

    .item {
      display: block;
      padding: 16px 20px;
      color: rgb(0 0 0 / 87%);
      border-bottom: 1px solid var(--color-grey-600);
      cursor: pointer;
      transition-duration: 0.2s;

      &:hover,
      &.active {
        font-weight: var(--font-weight-bold);
        background-color: var(--color-grey-800);

        @media (max-width: 768px) {
          background-color: var(--color-grey-600);
        }
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &.child-route {
      @media (max-width: 768px) {
        display: none;
      }
    }

    @media (max-width: 768px) {
      background-color: var(--color-grey-700);
    }
  }

  .setting-container {
    width: 100%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    box-shadow: none;
  }
}

.slide-fade-leave-to {
  transition:
    opacity 0s,
    transform 0s;
}

.deactivate-button {
  margin-top: auto;
  text-transform: none;
  color: rgb(0 0 0 / 87%);
  font-size: 16px;
}
</style>
